import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import API from "@/lib/api";
import OrgIcon from "@/components/OrgIcon";
import { JNBT_FALLBACK as FALLBACK } from "@/data/leaderboard";

const isOurs = (cat) => cat && (cat === "fine-tuned" || cat === "ours" || cat.startsWith("ours"));
const isBase = (cat) => cat && (cat === "baseline" || cat === "base");

const SUBJECT_EXAMPLES = [
  { subj: "Physics · mechanics", body: "A block slides down a frictionless incline of height h and sticks to an identical block resting at the bottom. Find their speed just after the collision.", chain: "Energy conservation to find speed at the foot, then momentum conservation for the inelastic collision. Two principles, in order." },
  { subj: "Chemistry · stoichiometry", body: "What mass of oxygen is needed to completely combust 16 g of methane?", chain: "Balance CH₄ + 2O₂ → CO₂ + 2H₂O, convert grams to moles, apply the 1:2 ratio, convert back. One mis-step and everything is wrong." },
  { subj: "Mathematics · calculus", body: "Find the local maximum value of f(x) = x³ − 3x.", chain: "Differentiate, solve f′(x) = 0, use the second derivative to tell a maximum from a minimum, then evaluate." },
];

export default function NalandaJEENEETDetailPage() {
  const [rows, setRows] = useState(FALLBACK);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/hf/nalandabench-leaderboard`).then(r => { if (r.data?.rows?.length) setRows(r.data.rows); }).catch(() => {});
  }, []);

  const topAcc  = Math.max(...rows.map(r => r.accuracy));
  const oursRow = rows.find(r => isOurs(r.category));
  const baseRow = rows.find(r => isBase(r.category));
  const vsBase  = oursRow?.vs_base ?? 6.3;
  const active  = rows[activeIdx] ?? rows[0];

  return (
    <div className="bg-[#0A0A0A]" style={{ paddingTop: "96px" }}>
      <Helmet>
        <title>NalandaJEENEET — JEE &amp; NEET Reasoning Benchmark · Nalandadata.ai</title>
        <meta name="description" content="NalandaJEENEET: verified-reward RL on 116K expert JEE/NEET questions. A 7B model trained on verified answers — the benchmark and results are public." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nalandadata.ai/benchmarks/nalanda-jeeneet" />
        <meta property="og:title" content="NalandaJEENEET — JEE & NEET Reasoning Benchmark · Nalandadata.ai" />
        <meta property="og:description" content="NalandaJEENEET: verified-reward RL on 116K expert JEE/NEET questions. A 7B model trained on verified answers — the benchmark and results are public." />
        <meta property="og:image" content="https://nalandadata.ai/og-social.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NalandaJEENEET — JEE & NEET Reasoning Benchmark · Nalandadata.ai" />
        <meta name="twitter:description" content="NalandaJEENEET: verified-reward RL on 116K expert JEE/NEET questions. A 7B model trained on verified answers — the benchmark and results are public." />
        <meta name="twitter:image" content="https://nalandadata.ai/og-social.png" />
      </Helmet>

      {/* Breadcrumb */}
      <div className="s-breadbar">
        <div className="s-wrap s-breadbar-inner">
          <Link to="/">Home</Link>
          <span style={{ opacity: .4 }}>/</span>
          <Link to="/benchmarks">Benchmarks</Link>
          <span style={{ opacity: .4 }}>/</span>
          <span>NalandaJEENEET</span>
        </div>
      </div>

      {/* Page header */}
      <div className="s-detail-header">
        <div className="s-wrap">
          <p className="s-eyebrow">Science &amp; Maths Reasoning · JEE / NEET</p>
          <h1>Teaching a small model to reason through <span className="hl">advanced JEE &amp; NEET problems.</span></h1>
          <p className="sub">116,831 expert-curated JEE and NEET questions with verified correct answers — used as reward signal, not training labels. The benchmark and fine-tuned model are public.</p>
        </div>
      </div>

      {/* Stat strip */}
      <div className="s-stat-strip">
        <div className="s-stat-item"><div className="s-stat-num">116,831</div><div className="s-stat-lbl">Expert-curated JEE &amp; NEET questions with verified correct answers used as reward signal.</div></div>
        <div className="s-stat-item"><div className="s-stat-num">785</div><div className="s-stat-lbl">Held-out benchmark questions across Physics, Chemistry, Mathematics, and Biology.</div></div>
        <div className="s-stat-item"><div className="s-stat-num">4</div><div className="s-stat-lbl">Subjects covered — Physics, Chemistry, Mathematics, Biology — at JEE/NEET difficulty.</div></div>
        <div className="s-stat-item"><div className="s-stat-num">7B</div><div className="s-stat-lbl">Parameter count. A small model, domain-specific training, public results.</div></div>
      </div>

      {/* Split panel */}
      <div className="s-split-panel">

        {/* LEFT: ranked model list */}
        <div className="s-sp-left">
          <div className="s-sp-head">
            <span className="s-sp-head-label">Leaderboard · Accuracy</span>
            <span className="s-sp-head-label">{rows.length} models</span>
          </div>
          {rows.map((row, i) => (
            <div
              key={row.model}
              className={`s-sp-row${i === activeIdx ? " active" : ""}${isOurs(row.category) ? " ours" : ""}`}
              onClick={() => setActiveIdx(i)}
            >
              <span className="s-sp-rank">{i + 1}</span>
              <div className="s-sp-row-main">
                <div className="s-sp-row-top">
                  <OrgIcon org={row.org} />
                  <span className="s-sp-model">{row.model}</span>
                  {isOurs(row.category) && <span className="s-sp-badge">ours · GRPO</span>}
                  {isBase(row.category) && <span className="s-sp-badge" style={{ color: "var(--muted-2)", background: "rgba(255,255,255,.06)" }}>base</span>}
                </div>
                <div className="s-sp-bar-track">
                  <div className="s-sp-bar-fill" style={{ width: `${((row.accuracy / topAcc) * 100).toFixed(1)}%` }} />
                </div>
              </div>
              <div className="s-sp-score-col">
                <div className="s-sp-score">{row.accuracy}%</div>
                <div className="s-sp-delta">{i === 0 ? "—" : `−${(topAcc - row.accuracy).toFixed(1)}`}</div>
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT: model detail */}
        <div className="s-sp-right">
          <div className="s-sp-detail-head">
            <OrgIcon org={active?.org ?? "Nalandadata"} />
            <span className="s-sp-detail-name">{active?.model}</span>
            <span style={{ color: "var(--muted-2)", margin: "0 4px" }}>|</span>
            <span className="s-sp-detail-score">{active?.accuracy}% acc</span>
          </div>
          <div style={{ fontSize: "12.5px", color: "var(--muted-2)", marginBottom: "18px" }}>
            {active?.n_samples ?? 785} MCQs · {active?.method}
            {active?.vs_base != null && active.vs_base > 0 && <> · <span style={{ color: "#7fc794" }}>+{active.vs_base}pp vs base</span></>}
          </div>

          <div className="s-sp-tabs">
            <button className="s-sp-tab active">Task</button>
          </div>
          {(() => {
            const ex = SUBJECT_EXAMPLES[activeIdx % SUBJECT_EXAMPLES.length];
            return (
              <div className="s-sp-task">
                <div className="s-sp-task-label">Sample question type · {ex.subj}</div>
                <div className="s-sp-task-body">{ex.body}</div>
                <div style={{ fontSize: "12.5px", color: "var(--muted-2)", marginTop: "10px", lineHeight: 1.6 }}>
                  <b style={{ color: "var(--muted)" }}>What it takes:</b> {ex.chain}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Reproducibility */}
      <section className="s-band">
        <div className="s-wrap">
          <p className="s-eyebrow">Reproducibility</p>
          <h2 style={{ fontSize: "clamp(22px,3vw,32px)", fontWeight: 800, letterSpacing: "-.025em", marginBottom: "12px" }}>Everything here is public.</h2>
          <p style={{ fontSize: "14.5px", color: "var(--muted)", marginBottom: "20px" }}>The fine-tuned model and a sample of the benchmark dataset are public. Submit your model to appear on the leaderboard.</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            <a className="s-btn ghost" href="https://huggingface.co/Nalandadata/nalanda-qwen-7b-grpo" target="_blank" rel="noopener noreferrer">Open the model on Hugging Face →</a>
            <a className="s-btn primary" href="https://huggingface.co/datasets/Nalandadata/NalandaJEENEETBench-sample" target="_blank" rel="noopener noreferrer">Download a data sample →</a>
          </div>
        </div>
      </section>

      {/* Full leaderboard */}
      <section className="s-band alt" id="results">
        <div className="s-wrap">
          <p className="s-eyebrow">Results</p>
          <h2 style={{ fontSize: "clamp(22px,3vw,32px)", fontWeight: 800, letterSpacing: "-.025em", marginBottom: "20px" }}>Full leaderboard</h2>
          {oursRow && baseRow && (
            <div className="s-key-stat">
              <span className="kd">+{vsBase}pp</span>
              <span className="kl"><b>from verified-reward RL (GRPO) on Qwen 2.5 7B</b> — {baseRow.accuracy}% → {oursRow.accuracy}% on {oursRow.n_samples ?? 785} held-out MCQs.</span>
            </div>
          )}
          <div className="s-methbox">
            <h4>Methodology</h4>
            <p>Expert ground-truth answers for every held-out question. Each model scored with an identical exact-match scorer. Decoding is deterministic (greedy / temperature 0). <a href="https://huggingface.co/datasets/Nalandadata/NalandaJEENEETBench" target="_blank" rel="noopener noreferrer">Full methodology &amp; benchmark on Hugging Face →</a></p>
          </div>
          <div className="s-lb-meta">
            <span className="lm">NalandaJEENEET · {oursRow?.n_samples ?? 785} held-out MCQs · {rows.length} models</span>
            <span className="ll">
              <a href="https://huggingface.co/datasets/Nalandadata/NalandaJEENEETBench" target="_blank" rel="noopener noreferrer">↗ HF dataset</a>
              <a href="https://huggingface.co/Nalandadata/nalanda-qwen-7b-grpo" target="_blank" rel="noopener noreferrer">↗ Model</a>
            </span>
          </div>
          <div className="s-tablecard">
            <div className="s-tbl-scroll">
              <table style={{ minWidth: "420px" }}>
                <caption>NalandaJEENEET — {oursRow?.n_samples ?? 785} held-out MCQs (accuracy %, higher is better)</caption>
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Method</th>
                    <th style={{ textAlign: "right" }}>Accuracy</th>
                    <th style={{ textAlign: "right" }}>vs base</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.model} className={isOurs(row.category) ? "best" : ""}>
                      <th>
                        {row.model}
                        {isOurs(row.category) && <span className="badge">ours</span>}
                        {isBase(row.category) && <span style={{ color: "var(--accent)", fontFamily: "var(--mono)", fontSize: "11px", marginLeft: "8px" }}>baseline</span>}
                      </th>
                      <td>{row.method}</td>
                      <td style={{ textAlign: "right" }}>{row.accuracy}%</td>
                      <td style={{ textAlign: "right" }}>{row.vs_base != null && row.vs_base > 0 ? <span className="s-dpos">+{row.vs_base}</span> : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p style={{ fontSize: "12.5px", color: "var(--muted-2)", marginTop: "16px", lineHeight: 1.6 }}>
            Leaderboard is live — results update automatically as new models are evaluated. <Link to="/contact" style={{ color: "var(--muted)" }}>Submit your model to appear here →</Link>
          </p>
        </div>
      </section>

      {/* The approach */}
      <section className="s-band" id="method">
        <div className="s-wrap">
          <p className="s-eyebrow">The approach</p>
          <h2 style={{ fontSize: "clamp(22px,3vw,32px)", fontWeight: 800, letterSpacing: "-.025em", marginBottom: "24px" }}>What we did, in four steps.</h2>
          <div className="s-dt-steps">
            <div className="s-dt-step"><div className="num">1</div><div><h5>Audit the archive</h5><p>128,832 raw questions. A keyword audit found <b>39.4% carried the wrong subject label</b>. Cleaning left 116,831 correctly labelled questions — this step alone, with no method change, was the difference between marginal and meaningful gains.</p></div></div>
            <div className="s-dt-step"><div className="num">2</div><div><h5>Light SFT warm-up</h5><p>200 steps on a <b>70/30 mix of domain and general data</b> (SlimOrca), conservative LoRA (rank 8, attention-only). Aggressive SFT on the domain data alone degraded the model significantly.</p></div></div>
            <div className="s-dt-step"><div className="num">3</div><div><h5>Verified-reward RL (GRPO)</h5><p>The model samples <b>eight answers per question</b> and is rewarded for reaching the verified-correct one. 600 steps on 10,000 multiple-choice questions — verified answers as the signal, not labels.</p></div></div>
            <div className="s-dt-step"><div className="num">4</div><div><h5>Evaluate on held-out set</h5><p><b>785 held-out MCQs</b> across Physics, Chemistry, Mathematics, Biology — questions the model never saw during training, scored identically for every model on the leaderboard.</p></div></div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="s-band alt s-cs-cta">
        <div className="s-wrap">
          <p className="s-eyebrow" style={{ justifyContent: "center" }}>Get on the leaderboard</p>
          <h2 style={{ fontSize: "clamp(25px,3.6vw,40px)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.08, margin: 0, textAlign: "center" }}>Run your model on NalandaJEENEET.</h2>
          <div className="s-cta-row">
            <a className="s-btn primary" href="https://huggingface.co/datasets/Nalandadata/NalandaJEENEETBench-sample" target="_blank" rel="noopener noreferrer">Download a sample</a>
            <Link className="s-btn ghost" to="/contact">Submit a model</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
